'use client';

import { useEffect, useState } from "react";
import { FaGripVertical, FaInfoCircle, FaSyncAlt, FaTimes } from "react-icons/fa";
import { useMouseState } from "../../MouseStateContext";
import styles from "./LeftButtonTest.module.css";

export default function LeftButtonTest() {
    const { mouse, updateMouse } = useMouseState();

    const [items, setItems] = useState([]);
    const [result, setResult] = useState(
        mouse?.tests?.find((test) => test.name === "LeftButtonTest")?.result || "initial"
    );
    const [testState, setTestState] = useState("initial");
    const [dragSourceIndex, setDragSourceIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const getItems = (count, offset = 0) =>
        Array.from({ length: count }, (_, k) => ({
            id: `item-${k + offset}`,
            content: k + 1,
        }));

    const shuffle = (number) => {
        const array = getItems(number);
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    const clearDragState = () => {
        setDragSourceIndex(null);
        setDragOverIndex(null);
    };

    useEffect(() => {
        setItems(shuffle(5));
    }, []);

    const setTestResult = (nextResult) => {
        setResult(nextResult);
        updateMouse({
            mouse: {
                ...mouse,
                messageShown: nextResult === "initial" ? false : mouse.messageShown,
                tests: mouse.tests.map((test) =>
                    test.name === "LeftButtonTest" ? { ...test, result: nextResult } : test
                ),
            },
        });
    };

    const checkOrdering = (array) => {
        const sorted = array.every((x, i) => i === 0 || x.content >= array[i - 1].content);
        if (sorted) setTestResult("success");
    };

    const reorder = (currentItems, startIndex, endIndex) => {
        const reordered = Array.from(currentItems);
        const [removed] = reordered.splice(startIndex, 1);
        reordered.splice(endIndex, 0, removed);
        return reordered;
    };

    const resetTest = () => {
        setTestState("initial");
        clearDragState();
        setItems(shuffle(5));
        setTestResult("initial");
    };

    const failTest = () => {
        clearDragState();
        setTestResult("failed");
    };

    const onDragStart = (e, index) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
        setTestState("started");
        setDragSourceIndex(index);
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    const onDrop = (e, index) => {
        e.preventDefault();
        if (dragSourceIndex === null || dragSourceIndex === index) {
            clearDragState();
            return;
        }
        const reorderedItems = reorder(items, dragSourceIndex, index);
        setItems(reorderedItems);
        checkOrdering(reorderedItems);
        clearDragState();
    };

    const correctCount = items.reduce(
        (acc, item, index) => acc + (item.content === index + 1 ? 1 : 0),
        0
    );

    const feedbackClass =
        result === "success" ? styles.isSuccess : testState === "started" ? styles.isActive : "";

    return (
        <div className={styles.wrapper}>
            <h2>Click &apos;n Drag Test</h2>
            <div className={styles.body}>
                <div className={styles.actionsRow}>
                    <span className={`${styles.statusPill} ${styles[`status-${result}`]}`}>
                        {result === "success" ? "Success" : result === "failed" ? "Failed" : "In progress"}
                    </span>
                    <div>
                        <button type="button" className="btn btn-sm btn-warning mx-1" onClick={resetTest}>
                            <FaSyncAlt /> Reset
                        </button>
                        <button type="button" className="btn btn-sm btn-danger mx-1" onClick={failTest}>
                            <FaTimes /> Fail
                        </button>
                    </div>
                </div>
                <div className={styles.info}>
                    <FaInfoCircle aria-hidden="true" /> Click and drag the numbers into numerical order.
                </div>
                <div className={styles.progressDots} aria-label={`${correctCount} of 5 in correct order`}>
                    {Array.from({ length: 5 }, (_, i) => (
                        <span
                            key={i}
                            className={`${styles.progressDot} ${i < correctCount ? styles.isFilled : ""} ${
                                result === "success" ? styles.isSuccessDot : ""
                            }`}
                        />
                    ))}
                </div>
                <div className={`${styles.feedback} ${feedbackClass}`}>
                    {result === "success" && "All numbers sorted — test passed!"}
                    {result === "failed" && "Test marked as failed."}
                    {result === "initial" && testState === "initial" && "Pending — drag to begin"}
                    {result === "initial" && testState === "started" && `Working… ${correctCount}/5 in place`}
                </div>
                <div className={styles.shell}>
                    {items.map((item, index) => {
                        const isDragging = dragSourceIndex === index;
                        const isDropTarget =
                            dragOverIndex === index && dragSourceIndex !== null && dragSourceIndex !== index;
                        return (
                            <div
                                key={item.id}
                                className={`${styles.draggableItem} ${styles[`result-${result}`]} ${
                                    isDragging ? styles.isDragging : ""
                                } ${isDropTarget ? styles.isDropTarget : ""}`}
                                draggable
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragOver={(e) => onDragOver(e, index)}
                                onDrop={(e) => onDrop(e, index)}
                                onDragEnd={clearDragState}
                            >
                                <span className={styles.dragGrip} aria-hidden="true">
                                    <FaGripVertical />
                                </span>
                                {item.content}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
